import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import type { ActingModifier } from '@/shared/api'
import { actingModeOf, useAccess, type AccessPair, type ActingMode } from '../access'
import { readActingModifier } from './modifier'
import { useRouteClient } from './route'

/**
 * Режим ресурса на текущей странице и модификатор для его запроса
 * (`docs/roles/03-frontend-architecture.md` §4–5). Модификатор читается из адреса только при
 * праве «от имени»: у клиента параметры чужой ссылки в запрос не уходят и `403` не вызывают.
 *
 * Клиент из пути профиля (`/clients/:client/…`) главнее `?client=`: вкладка профиля — те же
 * страницы, что и прежде, а клиент приходит из маршрута. Остальные параметры модификатора
 * (`account`, `unp`, `settlement`) по-прежнему живут в строке запроса.
 */
export function useActingMode(pair: AccessPair): {
  mode: ActingMode
  modifier: ActingModifier | undefined
} {
  const { can } = useAccess()
  const [params] = useSearchParams()
  const routeClient = useRouteClient()

  return useMemo(() => {
    const allowed = pair.onBehalf !== undefined && can(pair.onBehalf)
    let modifier: ActingModifier | undefined
    if (allowed) {
      const source = new URLSearchParams(params)
      if (routeClient !== undefined) source.set('client', routeClient)
      modifier = readActingModifier(source, pair)
    }
    return { mode: actingModeOf(pair, can, modifier !== undefined), modifier }
  }, [can, params, pair, routeClient])
}
